import { createFileRoute } from '@tanstack/react-router';
import { useAuthStore } from '../lib/auth-store';
import { useFinanceOps } from '../lib/landings';
import { EmptyState } from '../components/ui/EmptyState';
import {
  LandingPage,
  KPIRow,
  CardsRow,
  LandingSkeleton,
  LandingError,
  LandingGuard,
  KPICard,
  LandingCard,
  LandingCardLine,
  ModuleGatedCard,
} from '../components/portal-shell';
import { formatINR, timeAgo } from '../lib/format';

export const Route = createFileRoute('/_authed/finance-ops')({
  component: FinanceOpsPage,
});

function FinanceOpsPage() {
  return (
    <LandingGuard landingPath="/finance-ops">
      <FinanceOpsContent />
    </LandingGuard>
  );
}

function FinanceOpsContent() {
  const user = useAuthStore((s) => s.user);
  const query = useFinanceOps({ enabled: !!user });

  if (query.isLoading || (!query.data && !query.isError)) {
    return (
      <LandingPage
        title="Finance Operations"
        meta="Today's queue · Finance Admin"
        intro="Payments to confirm, parent-reported payments to check, withdrawals to approve."
      >
        <LandingSkeleton />
      </LandingPage>
    );
  }
  if (query.isError || !query.data) {
    return (
      <LandingPage title="Finance Operations">
        <LandingError error={query.error} onRetry={() => query.refetch()} />
      </LandingPage>
    );
  }

  const { data } = query.data;
  const hasActivity = data.recentActivity.length > 0;

  return (
    <LandingPage
      title="Finance Operations"
      meta={user ? `${user.displayName} · Finance Admin` : 'Finance Admin'}
      intro="Payments to confirm, parent-reported payments to check, withdrawals to approve."
    >
      <KPIRow>
        <KPICard
          label="Overdue"
          value={data.overdueSummary.count}
          footnote={formatINR(data.overdueSummary.totalPaise)}
          tone={data.overdueSummary.count > 0 ? 'warning' : 'success'}
        />
        <KPICard
          label="Reported payments to check"
          value={data.pendingClaims}
          footnote="Parent-submitted"
        />
        <KPICard
          label="Withdrawal requests"
          value={data.withdrawalQueue ? data.withdrawalQueue.count : '—'}
          footnote={
            data.withdrawalQueue === null
              ? 'Wallet module not enabled yet'
              : data.withdrawalQueue.count === 0
                ? 'Queue clear'
                : 'Awaiting approval'
          }
          newFor="/finance/withdrawals"
        />
        <KPICard
          label="Salary cycle"
          value={data.salaryCycleStatus ? `${data.salaryCycleStatus.unpaidCount}` : 'Idle'}
          footnote={
            data.salaryCycleStatus
              ? `Pay ${data.salaryCycleStatus.payoutDate} · ${data.salaryCycleStatus.unpaidCount} unpaid`
              : 'No active cycle'
          }
        />
      </KPIRow>

      {data.withdrawalQueue ? (
        data.withdrawalQueue.items.length > 0 ? (
          <LandingCard title="Withdrawal queue" newFor="/finance/withdrawals" className="md:col-span-2">
            {data.withdrawalQueue.items.map((item, idx, arr) => (
              <LandingCardLine key={item.id} last={idx === arr.length - 1}>
                <span className="font-medium">{item.requestedBy}</span>
                <span className="text-[color:var(--color-text-muted)]"> · </span>
                <span className="tabular-nums">{formatINR(item.amountPaise)}</span>
                <span className="text-[color:var(--color-text-muted)]">
                  {' '}
                  · requested {timeAgo(item.requestedAt)}
                </span>
              </LandingCardLine>
            ))}
          </LandingCard>
        ) : (
          <LandingCard title="Withdrawal queue" newFor="/finance/withdrawals" className="md:col-span-2">
            <EmptyState
              title="No pending withdrawals"
              description="Teacher and student withdrawals will appear here for your approval."
              className="py-6"
            />
          </LandingCard>
        )
      ) : (
        <ModuleGatedCard
          title="Withdrawal queue"
          variant="full"
          description="The Wallet & Referrals module is being rolled out. Withdrawal requests from teachers and students will land here when it's enabled."
        />
      )}

      <CardsRow>
        <LandingCard title="Recent activity">
          {hasActivity ? (
            data.recentActivity.slice(0, 6).map((entry, idx, arr) => (
              <LandingCardLine key={entry.id} last={idx === arr.length - 1}>
                <span className="font-medium">{entry.description}</span>
                <span className="text-[color:var(--color-text-muted)]">
                  {' '}
                  · {formatINR(entry.amountPaise)} · {timeAgo(entry.occurredAt)}
                </span>
              </LandingCardLine>
            ))
          ) : (
            <EmptyState
              title="No recent activity"
              description="New payments and salaries will show up here as they happen."
              className="py-6"
            />
          )}
        </LandingCard>
        <LandingCard title="Salary cycle">
          {data.salaryCycleStatus ? (
            <>
              <LandingCardLine>
                <span className="font-medium">
                  {String(data.salaryCycleStatus.year)} —{' '}
                  {monthName(data.salaryCycleStatus.month)}
                </span>
              </LandingCardLine>
              <LandingCardLine>
                Payout date:{' '}
                <span className="tabular-nums">{data.salaryCycleStatus.payoutDate}</span>
              </LandingCardLine>
              <LandingCardLine last>
                Unpaid staff:{' '}
                <span className="tabular-nums">{data.salaryCycleStatus.unpaidCount}</span>
              </LandingCardLine>
            </>
          ) : (
            <EmptyState
              title="No active cycle"
              description="Generate a salary cycle from the Salaries page."
              className="py-6"
            />
          )}
        </LandingCard>
      </CardsRow>
    </LandingPage>
  );
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const monthName = (m: number): string => MONTHS[Math.max(1, Math.min(12, m)) - 1] ?? String(m);
